import React, { useEffect, useState } from 'react';
import { Card, Timeline, Typography, Input, Button, message, Empty, Spin } from 'antd';
import { SendOutlined, UserOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, FileAddOutlined, FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ActivityLog } from '../../types';
import { activityLogApi } from '../../services/activityLogApi';
import { estimateApi } from '../../services/api';
import { ApprovalPDFService } from '../../services/approvalPdfService';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface ActivityLogProps {
    entityType: 'estimate' | 'work_order' | 'customer' | 'vehicle';
    entityId: number;
    height?: number | string;
}

export const ActivityLogComponent: React.FC<ActivityLogProps> = ({ entityType, entityId, height = 400 }) => {
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await activityLogApi.getByEntity(entityType, entityId);
            setLogs(response.data.sort((a, b) => new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime()));
        } catch (error) {
            console.error('Failed to fetch activity logs:', error);
            message.error('Failed to load activity logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (entityId) {
            fetchLogs();
        }
    }, [entityId, entityType]);

    const handleSubmitNote = async () => {
        if (!note.trim()) return;

        setSubmitting(true);
        try {
            await activityLogApi.create({
                entity_type: entityType,
                entity_id: entityId,
                action: 'note_added',
                description: note,
            });
            setNote('');
            message.success('Note added');
            fetchLogs(); // Refresh logs
        } catch (error) {
            console.error('Failed to add note:', error);
            message.error('Failed to add note');
        } finally {
            setSubmitting(false);
        }
    };

    const getIcon = (action: string) => {
        if (action.includes('approved')) return <CheckCircleOutlined style={{ color: 'green' }} />;
        if (action.includes('declined')) return <CloseCircleOutlined style={{ color: 'red' }} />;
        if (action.includes('created')) return <FileAddOutlined style={{ color: 'blue' }} />;
        if (action === 'email_sent') return <SendOutlined style={{ color: 'purple' }} />;
        if (action === 'note_added') return <UserOutlined style={{ color: 'orange' }} />;
        return <ClockCircleOutlined style={{ color: 'gray' }} />;
    };

    const getUserName = (log: ActivityLog) => {
        if (log.user_id) {
            return `${log.first_name || 'User'} ${log.last_name || ''}`.trim();
        }
        if (log.action.includes('approved') || log.action.includes('declined')) {
            return 'Customer';
        }
        return 'System';
    };

    const handleViewApprovalPDF = async (log: ActivityLog) => {
        if (entityType !== 'estimate') return;

        try {
            message.loading({ content: 'Generating PDF...', key: 'pdfGen' });
            // Fetch full estimate to get signature
            const response = await estimateApi.getById(entityId);
            const estimate = response.data;

            if (estimate) {
                await ApprovalPDFService.generateApprovalPDF(estimate);
                message.success({ content: 'PDF Downloaded', key: 'pdfGen' });
            } else {
                message.error({ content: 'Could not load estimate details', key: 'pdfGen' });
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error({ content: 'Failed to generate PDF', key: 'pdfGen' });
        }
    };

    return (
        <Card
            title={<Title level={5} style={{ margin: 0 }}>Activity Log</Title>}
            className="h-full shadow-sm"
            styles={{ body: { display: 'flex', flexDirection: 'column', height: '100%', padding: '12px', overflow: 'hidden' } }}
        >
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', maxHeight: height, paddingRight: '12px', paddingLeft: '4px', paddingTop: '4px' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <Spin />
                    </div>
                ) : logs.length === 0 ? (
                    <Empty description="No activity yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <Timeline
                        items={logs.map(log => ({
                            color: log.action.includes('declined') ? 'red' : log.action.includes('approved') ? 'green' : log.action === 'email_sent' ? 'purple' : 'blue',
                            dot: getIcon(log.action),
                            children: (
                                <div style={{
                                    background: '#fff',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    marginBottom: '8px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <div className="flex justify-between items-start" style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
                                        <div>
                                            <Text strong>{getUserName(log)}</Text>
                                        </div>
                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                            {dayjs(log.created_at).format('MMM D, h:mm A')}
                                        </Text>
                                    </div>
                                    <Text style={{ fontSize: '13px', color: '#4b5563' }}>{log.description}</Text>
                                    {((log.action === 'approved_with_changes' && log.metadata?.lineItemStatus) || (log.action === 'email_sent' && log.metadata?.recipient) || (log.action === 'approved')) && (
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px', background: '#f9fafb', padding: '6px', borderRadius: '4px', border: '1px solid #f3f4f6' }}>
                                            {/* Render specific metadata fields if needed, or just stringify for debug */}
                                            {/* For approvals, maybe show line item status summary? */}
                                            {log.action === 'approved_with_changes' && log.metadata.lineItemStatus && (
                                                <div>Approved items: {Object.keys(log.metadata.lineItemStatus).filter(k => log.metadata.lineItemStatus[k] === 'approved').length}</div>
                                            )}
                                            {log.action === 'email_sent' && log.metadata.recipient && (
                                                <div>To: {log.metadata.recipient}</div>
                                            )}

                                            {(log.action === 'approved' || log.action === 'approved_with_changes') && (
                                                <div style={{ marginTop: '8px', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        icon={<FilePdfOutlined />}
                                                        onClick={() => handleViewApprovalPDF(log)}
                                                        style={{ padding: 0, height: 'auto' }}
                                                    >
                                                        View Approval PDF
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ),
                        }))}
                    />
                )}
            </div>

            <div style={{ marginTop: 'auto', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <TextArea
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        placeholder="Add a note..."
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        onPressEnter={(e) => {
                            if (!e.shiftKey) {
                                e.preventDefault();
                                handleSubmitNote();
                            }
                        }}
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSubmitNote}
                        loading={submitting}
                    />
                </div>
            </div>
        </Card>
    );
};
